/**
 * Timeline de steps de ProvisioningJob (REDESIGN 2026-04-26).
 *
 * Adaptador sobre `LinhaDoTempoDePassos` (spec 2026-10-15 linha do tempo §4.3, D1): aqui
 * ficam só o que é do provisionamento de conta AWS — `stepMeta` (rótulo, ícone, contexto
 * por step_type), grupos Estrutura → Ambiente · email → Finalização, deep link do recurso
 * no console AWS e o retry por `step_index`. Forma e cores vêm do componente comum, que
 * segue o DS (em curso em `inovareTeal`, não azul; sem barra colorida no grupo).
 */

import { useMemo } from 'react'
import { ActionIcon, Anchor, Badge, Code, Tooltip } from '@mantine/core'
import { IconExternalLink, IconRefresh } from '@tabler/icons-react'
import type { ProvisioningStepResponse } from '@/api/awsOrgProvisioning'
import {
  LinhaDoTempoDePassos,
  type EstadoDoPasso,
  type GrupoDaLinha,
  type PassoDaLinha,
} from '@/components/common/LinhaDoTempoDePassos'
import { getStepMeta, parseStepInput, awsConsoleLink, formatDuration, groupSteps } from './stepMeta'

interface Props {
  steps: ProvisioningStepResponse[]
  onRetryFromStep: (stepIndex: number) => void
  retryDisabled?: boolean
}

const ESTADO: Record<string, EstadoDoPasso> = {
  completed: 'concluido',
  running: 'em_curso',
  failed: 'falhou',
  pending: 'pendente',
  skipped: 'pulado',
}

function recurso(step: ProvisioningStepResponse) {
  if (!step.aws_resource_id) return null
  const link = awsConsoleLink(step.step_type, step.aws_resource_id)
  const texto = truncate(step.aws_resource_id, 56)
  if (!link) return <Code style={{ fontSize: 11 }}>{texto}</Code>
  return (
    <Anchor href={link} target="_blank" rel="noopener noreferrer" underline="never">
      {/* Link é accent de ação: verde da marca (DS §2.1), não azul. */}
      <Code style={{ fontSize: 11, cursor: 'pointer', color: 'var(--mantine-color-inovareTeal-text)' }}>
        {texto} <IconExternalLink size={10} style={{ marginLeft: 2 }} />
      </Code>
    </Anchor>
  )
}

function paraPasso(step: ProvisioningStepResponse): PassoDaLinha {
  const meta = getStepMeta(step.step_type)
  const StepIcon = meta.icon
  return {
    chave: String(step.step_index),
    rotulo: meta.label,
    status: ESTADO[step.status] ?? 'pendente',
    prefixo: `#${step.step_index.toString().padStart(2, '0')}`,
    icone: <StepIcon size={14} color="var(--mantine-color-dimmed)" />,
    descricao: meta.describe(parseStepInput(step.step_input)) || undefined,
    dados: recurso(step),
    iniciado_em: step.started_at,
    concluido_em: step.completed_at,
    tentativas: step.attempt_count,
    erro: step.error_message || undefined,
    expansivel: step.aws_resource_id != null && step.aws_resource_id.length > 30,
    testId: `job-step-row-${step.step_index}`,
  }
}

export function JobStepTimeline({ steps, onRetryFromStep, retryDisabled = false }: Props) {
  const grupos = useMemo<GrupoDaLinha[]>(() => {
    const porIndice = new Map(steps.map((s) => [s.step_index, s]))
    return groupSteps(steps).map((g) => ({
      chave: g.id,
      rotulo: g.label,
      extra: g.emailDerivadoRecusado ? (
        <Tooltip
          label={`O endereço do padrão (${g.emailDerivadoRecusado}) já pertence a outra conta AWS; o provisionamento usou o do rótulo.`}
          withArrow
          multiline
          maw={320}
        >
          <Badge size="xs" variant="light" color="orange" tt="none" style={{ cursor: 'help' }}>
            endereço trocado
          </Badge>
        </Tooltip>
      ) : undefined,
      passos: g.stepIndices
        .map((i) => porIndice.get(i))
        .filter((s): s is ProvisioningStepResponse => Boolean(s))
        .map(paraPasso),
    }))
  }, [steps])

  return (
    <LinhaDoTempoDePassos
      grupos={grupos}
      formatarDuracao={formatDuration}
      rotuloDoErro="Erro no step"
      testId="job-step-timeline"
      acao={(p) =>
        p.status === 'falhou' ? (
          <Tooltip label="Tentar novamente a partir deste step" withArrow>
            <ActionIcon
              variant="light"
              size="md"
              onClick={() => onRetryFromStep(Number(p.chave))}
              disabled={retryDisabled}
              aria-label={`Retry step ${p.chave}`}
              data-testid={`job-step-retry-${p.chave}`}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        ) : null
      }
    />
  )
}

function truncate(s: string, max: number): string {
  if (!s) return ''
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}
